import { reactive, computed } from '@vue/composition-api';

import { useTeamId } from '@/modules/teamplan/utils/provideTeamId';
import {
  useProjectsBookmarksListWithSWRV,
  ProjectWithBookmarks,
} from '@/modules/teamplan/apis/useProjectsBookmarksListWithSWRV';
import {
  createTopicBookmark,
  destroyTopicBookmark,
} from '@/modules/teamplan/apis/projectsBookmarksApis';

import { toastError, toastSuccess } from '@/helpers/toast';
import { mixpanel } from '@/helpers/analytics';

export const useTopicBookmarkDropdownMenuToggleButton = (
  props: {
    topicId: number;
    project: ProjectWithBookmarks;
  },
  emit: (event: string) => void,
) => {
  const state = reactive({
    isLoading: false,
  });

  const teamId = useTeamId();
  const { mutations } = useProjectsBookmarksListWithSWRV(teamId, true);

  const isAdded = computed(() => {
    return props.project.topic_bookmarks.some(
      b => b.topic_id === props.topicId,
    );
  });

  const add = async () => {
    try {
      const {
        data: { id: topicBookmarkId },
      } = await createTopicBookmark(teamId, props.project.id, props.topicId);

      mutations.addTopicBookmark(props.project.id, {
        id: topicBookmarkId,
        topic_id: props.topicId,
      });

      toastSuccess(`トピックを「${props.project.name}」に追加しました`);
      emit('toggled');

      mixpanel.track('Bookmark Created', {
        page: location.pathname,
        is_sp: false,
        targetType: 'Topic',
      });
    } catch {
      toastError(
        `トピックを「${props.project.name}」に追加時にエラーが発生しました`,
      );
    }
  };

  const remove = async () => {
    const topicBookmark = props.project.topic_bookmarks.find(
      b => b.topic_id === props.topicId,
    )!;

    try {
      await destroyTopicBookmark(teamId, props.project.id, topicBookmark.id);

      mutations.removeTopicBookmark(props.project.id, topicBookmark.id);

      toastSuccess(`トピックを「${props.project.name}」から削除しました`);
      emit('toggled');

      mixpanel.track('Bookmark Dropped', {
        page: location.pathname,
        is_sp: false,
        target_type: 'Topic',
      });
    } catch {
      toastError(
        `トピックを「${props.project.name}」から削除時にエラーが発生しました`,
      );
    }
  };

  const toggle = async () => {
    if (state.isLoading) {
      return;
    }

    state.isLoading = true;

    if (!isAdded.value) {
      await add();
    } else {
      await remove();
    }

    state.isLoading = false;
  };

  return {
    state,
    isAdded,
    toggle,
  };
};
